/* eslint-disable no-throw-literal */
import gql from 'graphql-tag';
import apolloClient from '../../../../utils/apolloClient';

// ─── GraphQL Mutations ────────────────────────────────────────────────────────
//
// check_duplicate_invoice(input: CheckDuplicateInvoiceInput): CheckDuplicateInvoice
// generate_company_invoice(input: GenerateCompanyInvoiceInput): GenerateCompanyInvoice
//
// Note: the schema also has generate_invoice(input: GenerateInvoiceInput) but
// that takes only order_reference — it is a separate operation for order-level
// PDF generation and is NOT used on this page.

const CHECK_DUPLICATE_INVOICE = gql`
  mutation CheckDuplicateInvoice($input: CheckDuplicateInvoiceInput) {
    check_duplicate_invoice(input: $input) {
      exists
      error {
        code
        description
      }
    }
  }
`;

const GENERATE_COMPANY_INVOICE = gql`
  mutation GenerateCompanyInvoice($input: GenerateCompanyInvoiceInput) {
    generate_company_invoice(input: $input) {
      invoices {
        company_invoice_id
        invoice_number
        pdf_url
        csv_url
        admin_pdf_url
        admin_csv_url
      }
      company_with_no_data
      error {
        code
        description
      }
    }
  }
`;

// ─── Pure Transformers ────────────────────────────────────────────────────────

const toDuplicateCheck = raw => ({
  exists: raw?.exists === true,
});

const toGeneratedInvoices = raw => ({
  invoices: (raw?.invoices ?? []).map(item => ({
    companyInvoiceId: item?.company_invoice_id ?? null,
    invoiceNumber: item?.invoice_number ?? null,
    pdfUrl: item?.pdf_url ?? null,
    csvUrl: item?.csv_url ?? null,
    adminPdfUrl: item?.admin_pdf_url ?? null,
    adminCsvUrl: item?.admin_csv_url ?? null,
  })),
  companiesWithNoData: raw?.company_with_no_data ?? [],
});

// ─── Error Normalizer ─────────────────────────────────────────────────────────

const isAppError = err =>
  typeof err === 'object' && err !== null && 'code' in err && 'message' in err;

const toAppError = err => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('unauthorized') || msg.includes('unauthenticated')) {
      return { message: err.message, code: 'UNAUTHORIZED' };
    }
    if (msg.includes('network') || msg.includes('failed to fetch')) {
      return { message: err.message, code: 'NETWORK_ERROR' };
    }
    return { message: err.message, code: 'UNKNOWN_ERROR' };
  }
  return { message: 'An unexpected error occurred', code: 'UNKNOWN_ERROR' };
};

// ─── Service Implementation ───────────────────────────────────────────────────

export async function checkDuplicateInvoice(
  input,
  client = apolloClient.clientPrivate,
) {
  const { userId, companyId, fromDate, toDate } = input;

  if (!companyId) {
    throw {
      message: 'companyId is required to check for duplicate invoices',
      code: 'MISSING_COMPANY_ID',
    };
  }

  try {
    const { data } = await client.mutate({
      mutation: CHECK_DUPLICATE_INVOICE,
      variables: {
        input: {
          user_id: userId,
          company_id: companyId,
          from_date: fromDate,
          to_date: toDate,
        },
      },
    });

    const result = data?.check_duplicate_invoice;

    if (!result) {
      throw {
        message: 'No data returned from check_duplicate_invoice',
        code: 'PARSE_ERROR',
      };
    }

    if (result.error?.length) {
      throw {
        message: result.error[0]?.description ?? 'API error',
        code: 'API_ERROR',
      };
    }

    return toDuplicateCheck(result);
  } catch (err) {
    if (isAppError(err)) throw err;
    throw toAppError(err);
  }
}

export async function generateCompanyInvoice(
  input,
  client = apolloClient.clientPrivate,
) {
  const {
    userId,
    companyIds,
    fromDate,
    toDate,
    paymentDate,
    autoSendEmail,
    isCashInclude,
    isKraveinFeeInclude,
    allCompanies,
  } = input;

  if (!allCompanies && !companyIds?.length) {
    throw {
      message: 'At least one company must be selected to generate invoices',
      code: 'MISSING_COMPANY_ID',
    };
  }

  try {
    const { data } = await client.mutate({
      mutation: GENERATE_COMPANY_INVOICE,
      variables: {
        input: {
          user_id: userId,
          ...(allCompanies
            ? { is_all_company: true }
            : { company_id: companyIds }),
          from_date: fromDate,
          to_date: toDate,
          ...(paymentDate !== undefined ? { payment_date: paymentDate } : {}),
          ...(autoSendEmail !== undefined ? { sent_email: autoSendEmail } : {}),
          ...(isCashInclude !== undefined ? { is_cash_include: isCashInclude } : {}),
          ...(isKraveinFeeInclude !== undefined ? { is_kravein_fee_include: isKraveinFeeInclude } : {}),
        },
      },
    });

    const result = data?.generate_company_invoice;

    if (!result) {
      throw {
        message: 'No data returned from generate_company_invoice',
        code: 'PARSE_ERROR',
      };
    }

    if (result.error?.length) {
      throw {
        message: result.error[0]?.description ?? 'API error',
        code: 'API_ERROR',
      };
    }

    return toGeneratedInvoices(result);
  } catch (err) {
    if (isAppError(err)) throw err;
    throw toAppError(err);
  }
}

// ─── Export Pure Transformers ─────────────────────────────────────────────────

export { toDuplicateCheck, toGeneratedInvoices };

// ─── Export Service ───────────────────────────────────────────────────────────

export const generateInvoiceService = {
  checkDuplicateInvoice,
  generateCompanyInvoice,
};
